import { useState, useEffect, useCallback } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { ENTITY_ACTIVE, ENTITY_INACTIVE } from "../constants";
import { formatError } from "../utils/formatError";
import { fromDoc } from "../utils/firestoreDoc";
import logger from "../utils/logger";
import type { Entity } from "../types";

export function useEntities(enabled = true, agencyId: string | null = null) {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loading = enabled && !!agencyId && loadedFor !== agencyId;

  useEffect(() => {
    if (!enabled || !agencyId) return;
    setError(null);
    const q = query(
      collection(db, "entities"),
      where("agencyId", "==", agencyId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setEntities(snapshot.docs.map((d) => fromDoc<Entity>(d)));
        setLoadedFor(agencyId);
      },
      (err) => {
        logger.error("Failed to load entities:", err);
        setError(formatError(err));
        setLoadedFor(agencyId);
      }
    );
    return () => unsubscribe();
  }, [enabled, agencyId]);

  const createEntity = useCallback(async (data: Partial<Entity> & { agencyId: string }) => {
    try {
      await addDoc(collection(db, "entities"), {
        ...data,
        defaultPayRate: data.defaultPayRate ? Number(data.defaultPayRate) : null,
        status: ENTITY_ACTIVE,
        agencyId: data.agencyId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const updateEntity = useCallback(async (entityId: string, data: Partial<Entity>) => {
    try {
      await updateDoc(doc(db, "entities", entityId), {
        ...data,
        defaultPayRate: data.defaultPayRate ? Number(data.defaultPayRate) : null,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  const deactivateEntity = useCallback(async (entityId: string) => {
    try {
      await updateDoc(doc(db, "entities", entityId), {
        status: ENTITY_INACTIVE,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      throw new Error(formatError(err));
    }
  }, []);

  return { entities, loading, error, createEntity, updateEntity, deactivateEntity };
}
